/**
 * AUDIO DUCK — the one-voice law for SOURCE audio (2026-09-01, rewritten
 * 2026-09-04).
 *
 * Tour step videos carry embedded source audio (screen-recording sound,
 * clip music). The first cut kept the video ROLLING under the agent and
 * ducked its audio — but a clip that keeps playing while she explains it
 * runs ahead of her words, and the release always felt like a guess
 * (David, 2026-09-04: "just pause the video when she speaks, then duck as
 * it restarts"). The law now:
 *
 *   agent starts talking → PAUSE the active tour video (only one that is
 *                          rolling — a video the viewer paused stays theirs)
 *   agent stops          → hold ~800ms (inter-sentence gaps must not pump
 *                          the clip), then PLAY it back DUCKED and ramp the
 *                          source audio up to its original level (~1.2s) —
 *                          so a late tail of her voice never collides
 *
 * The same duck-in ramp is exported for the media floor's library-video
 * resume (content-panel.tsx) so every video restarts the same way.
 *
 * iOS Safari ignores writes to `video.volume` (read-only by policy) —
 * detected per element by writing and reading back; those restart at
 * full volume (a binary `muted` flip would just be a click).
 *
 * The active element is re-resolved continuously while the agent talks
 * (a screen advance mid-sentence swaps the <video> — the new one pauses
 * too), and every touched element's original volume is restored on
 * dispose without starting anything (nothing rolls into an unmount).
 */

export const DUCK_LEVEL = 0.12;
export const DUCK_RELEASE_HOLD_MS = 800;
/** Restart ramp: from DUCK_LEVEL back to the clip's own level. */
export const DUCK_IN_MS = 1200;
/** While the agent talks, re-resolve the active video this often (screen changes). */
export const DUCK_POLL_MS = 250;
const RAMP_STEP_MS = 30;

/** The slice of HTMLVideoElement the ducker touches — test seam. */
export type DuckableVideo = Pick<
  HTMLVideoElement,
  'volume' | 'muted' | 'currentTime' | 'paused'
> & {
  play: () => void | Promise<void>;
  pause: () => void;
};

export type TimerSeams = {
  setTimer?: (fn: () => void, ms: number) => ReturnType<typeof setTimeout>;
  clearTimer?: (t: ReturnType<typeof setTimeout>) => void;
  setTicker?: (fn: () => void, ms: number) => ReturnType<typeof setInterval>;
  clearTicker?: (t: ReturnType<typeof setInterval>) => void;
};

export type AudioDuckerDeps = TimerSeams & {
  /** The active tour screen video right now (may change between calls). */
  getVideo: () => DuckableVideo | null;
};

export type AudioDucker = ReturnType<typeof createAudioDucker>;

/** Volume writes the platform honours? (iOS Safari: read-only.) */
function volumeWritable(el: DuckableVideo): boolean {
  const before = el.volume;
  const probe = before > 0.5 ? before - 0.01 : before + 0.01;
  el.volume = probe;
  const ok = Math.abs(el.volume - probe) < 0.001;
  el.volume = before;
  return ok;
}

/**
 * Play `el` from a ducked level and ramp its source audio up to `base`.
 * Returns a cancel that restores `base` at once (the next pause, or an
 * unmount). A paused element mid-ramp ends the ramp on its own.
 */
export function playDuckedIn(
  el: DuckableVideo,
  base: number,
  seams: TimerSeams = {},
): () => void {
  const setTicker = seams.setTicker ?? ((fn, ms) => setInterval(fn, ms));
  const clearTicker = seams.clearTicker ?? ((t) => clearInterval(t));
  if (!volumeWritable(el)) {
    el.muted = false;
    void Promise.resolve(el.play()).catch(() => {});
    return () => {};
  }
  const target = Math.max(0, Math.min(1, base));
  el.volume = Math.min(target, DUCK_LEVEL);
  void Promise.resolve(el.play()).catch(() => {});
  const steps = Math.max(1, Math.round(DUCK_IN_MS / RAMP_STEP_MS));
  const delta = (target - el.volume) / steps;
  if (delta <= 0) {
    el.volume = target;
    return () => {};
  }
  let ticker: ReturnType<typeof setInterval> | null = setTicker(() => {
    if (el.paused) {
      stop();
      return;
    }
    el.volume = Math.min(target, el.volume + delta);
    if (el.volume >= target - 0.01) {
      el.volume = target;
      stop();
    }
  }, RAMP_STEP_MS);
  const stop = (): void => {
    if (ticker) {
      clearTicker(ticker);
      ticker = null;
    }
  };
  return () => {
    stop();
    el.volume = target;
  };
}

export function createAudioDucker(deps: AudioDuckerDeps) {
  const setTimer = deps.setTimer ?? ((fn, ms) => setTimeout(fn, ms));
  const clearTimer = deps.clearTimer ?? ((t) => clearTimeout(t));
  const setTicker = deps.setTicker ?? ((fn, ms) => setInterval(fn, ms));
  const clearTicker = deps.clearTicker ?? ((t) => clearInterval(t));

  let talking = false;
  let releaseTimer: ReturnType<typeof setTimeout> | null = null;
  let pollTicker: ReturnType<typeof setInterval> | null = null;
  /** Videos WE paused (the viewer's own pauses are never ours) → their level. */
  const held = new Map<DuckableVideo, number>();
  /** Duck-in ramps in flight → their cancel. */
  const ramps = new Map<DuckableVideo, () => void>();

  const cancelRamps = (): void => {
    for (const cancel of ramps.values()) cancel();
    ramps.clear();
  };

  /** The agent is talking: a rolling video pauses (idempotent, re-polled). */
  const holdElement = (el: DuckableVideo): void => {
    if (held.has(el)) return;
    const ramp = ramps.get(el);
    if (ramp) {
      // She spoke again mid-restart — the clip goes back on hold at its
      // own level, ready to duck in again.
      ramp();
      ramps.delete(el);
    } else if (el.paused) {
      return; // the viewer's pause — theirs to lift
    }
    held.set(el, el.volume);
    el.pause();
  };

  const releaseAll = (): void => {
    for (const [el, base] of held) {
      ramps.set(el, playDuckedIn(el, base, deps));
    }
    held.clear();
  };

  return {
    /** Follow the agent's audible-speech edges. */
    setDucked(next: boolean): void {
      if (next) {
        if (releaseTimer) {
          clearTimer(releaseTimer);
          releaseTimer = null;
        }
        if (talking) return;
        talking = true;
        const el = deps.getVideo();
        if (el) holdElement(el);
        // Keep the CURRENT video governed across screen changes — a video
        // step arriving mid-sentence pauses until she is done.
        pollTicker = setTicker(() => {
          const v = deps.getVideo();
          if (v) holdElement(v);
        }, DUCK_POLL_MS);
      } else {
        if (!talking || releaseTimer) return;
        releaseTimer = setTimer(() => {
          releaseTimer = null;
          talking = false;
          if (pollTicker) {
            clearTicker(pollTicker);
            pollTicker = null;
          }
          releaseAll();
        }, DUCK_RELEASE_HOLD_MS);
      }
    },
    dispose(): void {
      if (releaseTimer) clearTimer(releaseTimer);
      if (pollTicker) clearTicker(pollTicker);
      cancelRamps();
      // Restore levels; held clips stay paused (nothing rolls into an unmount).
      for (const [el, base] of held) el.volume = base;
      held.clear();
    },
  };
}
